import React from 'react';
import { Box, VStack, HStack, Text, Icon, Badge } from '@chakra-ui/react';
import { FiInfo, FiArrowRight } from 'react-icons/fi';
import { LuReceiptEuro } from 'react-icons/lu';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '../../hooks/useTranslation';
import { useGetCustomerQuery } from '../../api';
import MailActionCard from '../../components/mail/MailActionCard';
import Button from '../../components/base/Button';
import LoadingState from '../../components/base/LoadingState';
import { GrDocumentVerified } from 'react-icons/gr';
import PageTitle from '../../components/base/PageTitle';
import { useTariff } from '../../hooks/useTariff';
import { modalManager } from '../../utils/modalManager';
import { PaySubscriptionGate } from '../../components/base/PaySubscriptionGate';

const NEXT_CHARGE_INVALID_SENTINEL = '0001-01-02';

function isValidNextChargeDate(value: string | undefined | null): boolean {
    if (value == null || typeof value !== 'string') return false;
    const datePart = value.trim().slice(0, 10);
    if (!datePart) return false;
    return datePart !== NEXT_CHARGE_INVALID_SENTINEL;
}

const PaymentPage: React.FC = () => {
    const { t, locale } = useTranslation();
    const navigate = useNavigate();
    const { data: customer, isLoading } = useGetCustomerQuery();
    const { tariff, tariffType } = useTariff();

    const nextChargeFormatted = isValidNextChargeDate(customer?.next_charge)
        ? new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'long' }).format(
              new Date(customer?.next_charge ?? ''),
          )
        : null;

    const handleShowContract = () => {
        if (customer?.contract_link) {
            window.open(customer.contract_link, '_blank', 'noopener,noreferrer');
        }
    };

    const handleReplenish = () => {
        navigate('/payment/pay');
    };

    const handleProformaHistory = () => {
        if (customer?.status === 'new') {
            modalManager.show({
                title: t.pages.payment.chargesHistory,
                size: 'md',
                content: <PaySubscriptionGate onPay={() => modalManager.remove()} />,
            });
            return;
        }
        navigate('/payment/charges');
    };

    const hasTariffInfo = tariffType === 'determined';
    const handleTariffClick = () => {
        if (hasTariffInfo || tariffType === 'not_set') {
            navigate('/payment/tariff');
        }
    };

    if (isLoading || !customer) {
        return <LoadingState message={t.pages.support.loading} />;
    }

    // Форматируем баланс
    const formattedBalance = `€${customer.balance.toFixed(2)}`;

    const tariffPrice = `€${tariff.price_with_vat} ${t.pages.payment.perMonth}`;

    return (
        <Box
            h="calc(100vh - 20px)"
            bg="gray.100"
            px={4}
            pt={2}
            maxW="4xl"
            mx="auto"
            pb={{ base: 20, lg: 6 }}
            overflow="auto"
        >
            <VStack gap={6} align="stretch" maxW={{ base: 'full', md: '600px' }} mx="auto">
                {/* Заголовок */}
                <PageTitle title={t.pages.payment.title} />

                <HStack gap={4} align="stretch" w={{ base: 'full', md: '600px' }} mx="auto">
                    {/* Кнопка показа договора */}
                    <MailActionCard
                        icon={GrDocumentVerified}
                        label={t.pages.payment.showContract}
                        onClick={handleShowContract}
                    />

                    {/* История списаний */}
                    <MailActionCard
                        icon={LuReceiptEuro}
                        label={t.pages.payment.chargesHistory}
                        onClick={handleProformaHistory}
                    />
                </HStack>

                {/* Секция баланса и тарифа */}
                <Box p={6} bg="white">
                    <HStack gap={6} align="stretch">
                        {/* Баланс */}
                        <Box flex={1}>
                            <HStack gap={2} align="center" mb={2}>
                                <Text fontSize="md" color="gray.600" fontWeight="medium">
                                    {t.pages.payment.balance}
                                </Text>
                            </HStack>
                            <Text
                                fontSize="3xl"
                                color={customer.balance >= 0 ? 'green.500' : 'red.500'}
                                fontWeight="bold"
                            >
                                {formattedBalance}
                            </Text>
                        </Box>

                        {/* Тариф */}
                        <Box
                            flex={1}
                            cursor={hasTariffInfo ? 'pointer' : 'default'}
                            onClick={handleTariffClick}
                            _hover={hasTariffInfo ? { opacity: 0.8 } : undefined}
                            transition="opacity 0.2s"
                        >
                            <HStack gap={2} align="center" mb={2}>
                                <Text fontSize="md" color="gray.600" fontWeight="medium">
                                    {t.pages.payment.tariff}
                                </Text>
                                <Box>
                                    <Icon as={FiInfo} boxSize={4} color="yellow.600" />
                                </Box>
                            </HStack>
                            <Text fontSize="lg" color="black" fontWeight="medium" mb={1}>
                                {tariff.name}
                            </Text>
                            {tariffType !== 'not_set' && (
                                <Text fontSize="sm" color="gray.600">
                                    {tariffPrice}
                                </Text>
                            )}
                            {tariffType !== 'not_set' &&
                                (nextChargeFormatted ? (
                                    <HStack
                                        gap={1}
                                        mt={2}
                                        fontSize="sm"
                                        color="gray.600"
                                        flexWrap="wrap"
                                        align="baseline"
                                    >
                                        <Text as="span">{t.pages.payment.nextChargeDate}</Text>
                                        <Text as="span" whiteSpace="nowrap">
                                            {nextChargeFormatted}
                                        </Text>
                                    </HStack>
                                ) : (
                                    <Badge
                                        mt={2}
                                        colorPalette="red"
                                        borderRadius="0"
                                        variant="subtle"
                                        w="fit-content"
                                    >
                                        {t.pages.payment.tariffNotActivated}
                                    </Badge>
                                ))}
                        </Box>
                    </HStack>
                </Box>

                {/* Секция пополнения баланса */}
                <Box p={6} bg="white">
                    <Text fontSize="lg" color="black" fontWeight="medium" mb={4}>
                        {t.pages.payment.replenishBalance}
                    </Text>
                    <HStack gap={3} align="flex-end" mb={4}>
                        <Button buttonType="success" onClick={handleReplenish} w="full" h="48px">
                            <HStack gap={2}>
                                <Text>{t.pages.payment.replenish}</Text>
                                <Icon as={FiArrowRight} boxSize={4} />
                            </HStack>
                        </Button>
                    </HStack>
                    <VStack align="stretch" gap={1}>
                        <Text fontSize="sm" color="gray.600">
                            {t.pages.payment.paymentCredited}
                        </Text>
                    </VStack>
                </Box>
            </VStack>
        </Box>
    );
};

export default PaymentPage;
